import type { FC } from 'react';

import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalTitle,
} from '@mit/ui-components/right-tower';
import { useAuth } from '@site-builder/block-helpers';

import * as style from './composer-club-modal.css';
import { CartIcon, MinusIcon, PlusIcon } from '../../assets/icons';
import { formatNumber, FormattedMessage, isItemPurchased } from '../../utils';
import { useStoreCard } from '../store-card-wrapper/store-card-context';

import type { Item, Bundle } from '@site-builder/store-sdk';

const PURCHASE_BUTTON_IMAGE_URL =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/278351b94c153d7a031eba9650ab1fa7.png@webp';

interface ComposerClubModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly bundleData?: Bundle | null;
}

export const ComposerClubModal: FC<ComposerClubModalProps> = ({
  isOpen,
  onClose,
  bundleData,
}) => {
  const {
    item,
    quantityInCart,
    availableAmount,
    onPurchase,
    onAddToCart,
    onInCartDecrement,
    onInCartIncrement,
  } = useStoreCard();
  const [{ isAuthorized }, { logIn }] = useAuth();

  const isPurchased = isItemPurchased(item as Item);
  const showAmountPicker = quantityInCart > 0;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleBuyClick = () => {
    if (!isAuthorized) {
      logIn();
      return;
    }
    onPurchase?.();
  };

  const handleCartClick = () => {
    if (!isAuthorized) {
      logIn();
      return;
    }
    onAddToCart();
  };

  const handleDecrement = () => {
    onInCartDecrement();
  };

  const handleIncrement = () => {
    onInCartIncrement();
  };

  const canIncrement = availableAmount > 0 && quantityInCart < availableAmount;
  const isDisabled = isPurchased;

  return (
    <Modal open={isOpen} onOpenChange={handleOpenChange}>
      <ModalOverlay className={style.modalOverlay}>
        <ModalContent
          className={`${style.modalContent} msm-frame msm-frame--brown`}
        >
          {bundleData ? (
            <>
              <button
                className={style.modalClose}
                onClick={onClose}
                type='button'
                aria-label='Close modal'
              >
                <img
                  src={PURCHASE_BUTTON_IMAGE_URL}
                  alt='Purchase button'
                  className={style.modalButtonImage}
                />
              </button>

              <div className={style.modalHeader}>
                {bundleData.imageUrl && (
                  <div className={style.modalHeroImageWrapper}>
                    <img
                      src={bundleData.imageUrl}
                      alt={bundleData.name}
                      className={style.modalHeroImage}
                    />
                  </div>
                )}
                <div className={style.modalHeaderText}>
                  <ModalTitle className={style.modalTitle}>
                    {bundleData.name}
                  </ModalTitle>
                </div>
              </div>

              <div className={style.modalBody}>
                {bundleData.type === 'bundle' &&
                  'content' in bundleData &&
                  bundleData.content &&
                  (() => {
                    const items = bundleData.content as {
                      originalItem: {
                        sku: string;
                        name: string;
                        imageUrl?: string;
                      };
                      quantity: number;
                    }[];

                    const renderItem = (
                      contentItem: (typeof items)[number],
                      index: number,
                    ) => (
                      <div
                        key={contentItem.originalItem.sku || index}
                        className={style.modalBonusItem}
                      >
                        {contentItem.originalItem.imageUrl && (
                          <img
                            src={contentItem.originalItem.imageUrl}
                            alt={contentItem.originalItem.name}
                            className={style.modalBonusIcon}
                          />
                        )}
                        <div className={style.modalBonusText}>
                          <p className={style.modalBonusName}>
                            {contentItem.originalItem.name}
                          </p>
                          <p className={style.modalBonusAmount}>
                            x{formatNumber(contentItem.quantity)}
                          </p>
                        </div>
                      </div>
                    );

                    if (items.length < 5) {
                      return (
                        <div className={style.modalBonuses}>
                          {items.map(renderItem)}
                        </div>
                      );
                    }

                    const [daily, ...rest] = items;
                    const weekly = rest.slice(0, 2);
                    const monthly = rest.slice(2, 4);

                    return (
                      <div className={style.modalBonusGroups}>
                        <p className={style.modalGroupLabel}>
                          <FormattedMessage id='composer-club.daily' />
                        </p>
                        <div className={style.modalBonuses}>
                          {renderItem(daily, 0)}
                        </div>

                        <p className={style.modalGroupLabel}>
                          <FormattedMessage id='composer-club.weekly' />
                        </p>
                        <div className={style.modalBonuses}>
                          {weekly.map(renderItem)}
                        </div>

                        <p className={style.modalGroupLabel}>
                          <FormattedMessage id='composer-club.monthly' />
                        </p>
                        <div className={style.modalBonuses}>
                          {monthly.map(renderItem)}
                        </div>
                      </div>
                    );
                  })()}

                <div className={style.modalFooter}>
                  <div className={style.modalButtonsContainer}>
                    {showAmountPicker && !isPurchased ? (
                      <div className={`${style.modalAmountPicker} msm-button`}>
                        <button
                          type='button'
                          onClick={handleDecrement}
                          className={style.modalAmountAdjustButton}
                        >
                          <MinusIcon />
                        </button>
                        <span className={style.modalAmountCount}>
                          {quantityInCart}
                        </span>
                        <button
                          type='button'
                          onClick={handleIncrement}
                          disabled={!canIncrement}
                          className={style.modalAmountAdjustButton}
                        >
                          <PlusIcon />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type='button'
                          className={`${style.modalBuyButton} msm-button`}
                          title='Buy'
                          onClick={handleBuyClick}
                          disabled={isDisabled}
                        >
                          {bundleData.price ? (
                            `${bundleData.price.currency === 'USD' ? '$' : bundleData.price.currency}${bundleData.price.amount}`
                          ) : (
                            <FormattedMessage id='composer-club.no-price-available' />
                          )}
                        </button>
                        <button
                          type='button'
                          className={`${style.modalCartButton} msm-button`}
                          title='Add to cart'
                          onClick={handleCartClick}
                          disabled={isDisabled}
                        >
                          <CartIcon />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};
